import { gql } from '@apollo/client';
import { Checkbox, useToast } from '@chakra-ui/react';
import { cloneDeep, isNil, merge } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrightnessIcon } from '~components/ui/icons/BrightnessIcon';
import { Slider } from '~components/ui/Slider';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import type { DisplayBrightnessV2Slider_DisplayFragment } from './__generated__/DisplayBrightnessV2Slider.graphql';
import { useBulkUpdateBrightnessV2Mutation } from './__generated__/DisplayBrightnessV2Slider.graphql';

interface Props {
  display: DisplayBrightnessV2Slider_DisplayFragment;
  isDisabled?: boolean;
}

export function DisplayBrightnessV2Slider({ display, isDisabled = false }: Props) {
  const toast = useToast();
  const [brightnessLevel, setBrightnessLevel] = useState(0);
  const [autoBrightness, setAutoBrightness] = useState(false);

  const analytics = useAnalyticsReporter();
  const { t } = useTranslation();

  useEffect(() => {
    const newBrightnessLevel =
      display.brightnessV2?.desired?.value ?? display.brightnessV2?.reported?.value;

    if (!isNil(newBrightnessLevel)) {
      setBrightnessLevel(newBrightnessLevel);
    }
    const autoBrightness =
      display.brightnessV2?.desired?.autoBrightness ??
      display.brightnessV2?.reported?.autoBrightness;

    if (!isNil(autoBrightness)) {
      setAutoBrightness(autoBrightness);
    }
  }, [
    display.brightnessV2?.desired?.value,
    display.brightnessV2?.reported?.value,
    display.brightnessV2?.reported?.autoBrightness,
    display.brightnessV2?.desired?.autoBrightness,
  ]);

  const [bulkUpdateBrightnessV2] = useBulkUpdateBrightnessV2Mutation();

  const handleUpdate = useCallback(
    async (value: number, auto: boolean) => {
      try {
        await bulkUpdateBrightnessV2({
          variables: {
            input: {
              displayIds: [display.id],
              brightness: value,
              autoBrightness: auto,
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            displayBulkUpdateBrightnessV2: {
              __typename: 'DisplayBulkUpdateBrightnessV2Payload',
              displays: [
                {
                  __typename: 'Display',
                  id: display.id,
                  brightnessV2: merge(cloneDeep(display.brightnessV2), {
                    desired: value,
                    autoBrightness: auto,
                  }),
                },
              ],
            },
          },
        });

        analytics.track('displaySettingUpdate', {
          group: 'playback',
          changeItem: 'brightnessV2',
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('brightnessUpdateErr'),
          description: fromError(err, 'BulkUpdateBrightness', {
            BRIGHTNESS_OUT_OF_RANGE: t('brightnessUpdateErrDesc'),
          }),
        });
      }
    },
    [analytics, bulkUpdateBrightnessV2, display, toast, t],
  );

  const handleSliderChangeEnd = (value: number) => {
    handleUpdate(value, autoBrightness);
  };

  const handleAutoBrightnessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setAutoBrightness(checked);

    // trigger update immediately when toggled
    handleUpdate(brightnessLevel, checked);
  };

  return (
    <>
      <Checkbox
        isChecked={autoBrightness}
        onChange={handleAutoBrightnessChange}
        mb={2}
        isDisabled={isDisabled}
      >
        {t('autoBrightness')}
      </Checkbox>

      <Slider
        value={brightnessLevel}
        onChange={setBrightnessLevel}
        onChangeEnd={handleSliderChangeEnd}
        leftIcon={BrightnessIcon}
        min={0}
        max={100}
        isDisabled={isDisabled || autoBrightness}
      />
    </>
  );
}

DisplayBrightnessV2Slider.graphql = {
  fragment: {
    DisplayBrightnessV2Slider_display: gql`
      fragment DisplayBrightnessV2Slider_display on Display {
        id
        brightnessV2 {
          reported {
            value
            autoBrightness
          }
          desired {
            value
            autoBrightness
          }
        }
      }
    `,
  },
  mutations: {
    BulkUpdateBrightnessV2: gql`
      mutation BulkUpdateBrightnessV2($input: DisplayBulkUpdateBrightnessV2Input!) {
        displayBulkUpdateBrightnessV2(input: $input) {
          displays {
            id
            ...DisplayBrightnessV2Slider_display
          }
        }
      }
    `,
  },
};
